import React from "react";
import { Grid, TextField, Box, CircularProgress, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { FormattedMessage } from "react-intl";
import { debounce } from "../../util";
import { Autocomplete } from "@material-ui/lab";
import { getUsersBelow } from "../../actions";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  containerTitle: {
    marginBottom: theme.spacing(0.7),
  },
  selectedOption: {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  defaultOption: {
    color: "inherit",
    fontWeight: 400,
  },
  tagText: {
    cursor: "pointer",
    marginLeft: 6,
    maxWidth: 120,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  autocomplete: {
    "& .MuiAutocomplete-inputRoot": {
      maxHeight: 80,
      overflowY: "auto",
      flexWrap: "nowrap",
    },
  },
}));

const SearchByNameOrId = ({ label, selectedUsers, setSelectedUsers, title, roles, enableSearchById }) => {
  const classes = useStyles();

  const [inputValue, setInputValue] = React.useState({
    name: "",
    id: "",
  });

  const [userOptions, setUserOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const fetchUsers = React.useCallback(
    async (prop, value) => {
      if (!value) return setUserOptions([]);
      setLoading(true);
      try {
        const users = await getUsersBelow({ [prop]: value, skip: 0, take: 20, role: roles });
        setUserOptions(users || []);
      } catch {
        setUserOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [roles],
  );

  const debouncedFetchName = React.useMemo(() => debounce((value) => fetchUsers("nameOrEmail", value), 500), []);
  const debouncedFetchId = React.useMemo(() => debounce((value) => fetchUsers("userId", value), 500), []);

  const handleNameInput = (event, value) => {
    setInputValue({ name: value, id: "" });
    if (value) debouncedFetchName(value);
  };

  const handleIdInput = (event, value) => {
    const newValue = value.replace(/\D/g, "");
    setInputValue({ id: newValue, name: "" });
    if (newValue) debouncedFetchId(newValue);
  };

  const sortedOptions = React.useMemo(() => {
    const selectedIds = new Set(selectedUsers.map((u) => u.id));
    const selected = selectedUsers;
    const others = userOptions.filter((u) => !selectedIds.has(u.id));

    return [...selected, ...others];
  }, [userOptions, selectedUsers]);

  const handleSelectChange = (newValue) => {
    const uniqueUsers = [];
    const ids = new Set();

    newValue.forEach((user) => {
      if (!ids.has(user.id)) {
        ids.add(user.id);
        uniqueUsers.push(user);
      }
    });

    setSelectedUsers(uniqueUsers);
  };

  return (
    <Box className={classes.container}>
      {title && (
        <Typography
          variant="body1"
          className={classes.containerTitle}
        >
          <FormattedMessage id={title} />
        </Typography>
      )}

      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={8}
        >
          <Autocomplete
            multiple
            options={sortedOptions}
            value={selectedUsers}
            disableCloseOnSelect
            onChange={(event, newValue) => handleSelectChange(newValue)}
            inputValue={inputValue.name}
            onInputChange={handleNameInput}
            getOptionLabel={(option) => `${option.name} (${option.id})`}
            loading={loading}
            size="small"
            className={classes.autocomplete}
            noOptionsText={
              loading ? <FormattedMessage id="app.loading.fetching.user" /> : inputValue.name ? <FormattedMessage id="app.user.notFound" /> : <FormattedMessage id="app.autocomplete.searchUser" />
            }
            renderTags={(value) =>
              value.map((option, index) => (
                <React.Fragment key={option.id}>
                  <Typography
                    component="span"
                    className={classes.tagText}
                  >
                    {option.name}
                  </Typography>
                  <span> ({option.id})</span>
                  {index < value.length - 1 ? "," : ""}
                </React.Fragment>
              ))
            }
            renderInput={(props) => (
              <TextField
                {...props}
                label={<FormattedMessage id={label} />}
                variant="outlined"
                fullWidth
                InputProps={{
                  ...props.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress size={20} />}
                      {props.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(option) => {
              const isSelected = selectedUsers.some((user) => user.id === option.id);
              return (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  key={option.id}
                >
                  <Box
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    className={isSelected ? classes.selectedOption : classes.defaultOption}
                  >
                    {option.name}
                  </Box>
                  <Box
                    ml={2}
                    className={isSelected ? classes.selectedOption : classes.defaultOption}
                  >
                    ({option.id})
                  </Box>
                </Box>
              );
            }}
          />
        </Grid>

        {enableSearchById && (
          <Grid
            item
            xs={4}
          >
            <Autocomplete
              multiple
              options={sortedOptions}
              value={selectedUsers}
              disableCloseOnSelect
              onChange={(event, newValue) => handleSelectChange(newValue)}
              inputValue={inputValue.id}
              onInputChange={handleIdInput}
              getOptionLabel={(o) => `${o.name} (${o.id})`}
              loading={loading}
              size="small"
              renderTags={() => []}
              noOptionsText={
                loading ? <FormattedMessage id="app.loading.fetching.user" /> : inputValue.id ? <FormattedMessage id="app.user.notFound" /> : <FormattedMessage id="app.autocomplete.searchUser" />
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={<FormattedMessage id="app.search.id" />}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default React.memo(SearchByNameOrId);
